// frontend/src/screens/RoomScreen.jsx
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ChatRoom, { genderEmoji } from '../components/ChatRoom';


const SIGN_EMOJI = {
  aries: '♈', tauro: '♉', geminis: '♊', 'géminis': '♊', cancer: '♋', 'cáncer': '♋',
  leo: '♌', virgo: '♍', libra: '♎', escorpio: '♏', sagitario: '♐',
  capricornio: '♑', acuario: '♒', piscis: '♓'
};

const HOME_ZODIAC_KEY_BY_ROOM_SIGN = {
  aries: 'aries',
  tauro: 'taurus',
  geminis: 'gemini',
  cancer: 'cancer',
  leo: 'leo',
  virgo: 'virgo',
  libra: 'libra',
  escorpio: 'scorpio',
  sagitario: 'sagittarius',
  capricornio: 'capricorn',
  acuario: 'aquarius',
  piscis: 'pisces',
};

function emojiFor(sign) {
  if (!sign) return '';
  const key = sign.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return SIGN_EMOJI[key] || '';
}

// Normaliza el nombre de la sala para usar como room id
function roomIdFor(signName) {
  if (!signName) return '';
  const slug = String(signName)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // quitar acentos
    .toLowerCase()
    .replace(/\s+/g, '-')            // espacios -> guión
    .replace(/[^a-z0-9-]/g, '');
  return `room:${slug}`;// limpiar resto
}

const RoomScreen = () => {
  const { t } = useTranslation(['rooms', 'common', 'home']);
  const { signo } = useParams();
  const navigate = useNavigate();

  const roomId = roomIdFor(signo);

  React.useEffect(() => {
    const HOME_RETURN_BG = 'radial-gradient(circle at 48% -10%, rgba(255,215,0,.18), transparent 25%), radial-gradient(circle at 14% 17%, rgba(157,66,255,.23), transparent 34%), radial-gradient(circle at 85% 35%, rgba(218,165,32,.13), transparent 30%), radial-gradient(circle at 50% 74%, rgba(91,0,145,.28), transparent 44%), linear-gradient(180deg, #070013 0%, #15002d 38%, #090016 100%)';

    document.body.classList.add('room-page');

    return () => {
      document.body.classList.remove('room-page');

      const viewportHeight = Math.max(
        window.visualViewport?.height || 0,
        window.innerHeight || 0,
        document.documentElement.clientHeight || 0
      );

      if (viewportHeight > 0) {
        document.documentElement.style.setProperty('--zdx-home-vh', `${Math.round(viewportHeight)}px`);
      }

      document.documentElement.style.background = HOME_RETURN_BG;
      document.documentElement.style.backgroundColor = '#090016';
      document.body.style.background = HOME_RETURN_BG;
      document.body.style.backgroundColor = '#090016';
    };
  }, []);

  const goBackHome = () => navigate('/rooms');


  const normalizedSigno = signo
    ? signo.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    : '';
  const zodiacTranslationKey = HOME_ZODIAC_KEY_BY_ROOM_SIGN[normalizedSigno];
  const displaySigno = zodiacTranslationKey
    ? t(`home:zodiacSigns.${zodiacTranslationKey}`)
    : (signo ? signo.charAt(0).toUpperCase() + signo.slice(1) : '');
  const emoji = emojiFor(signo);
  

  const goToInfo = () => {
    navigate(`/sign-info/${encodeURIComponent(signo)}`, {
      state: { from: `/room/${encodeURIComponent(signo)}` },
    });
  };

  const [gEmoji] = React.useState(() => {
    try {
      const raw = localStorage.getItem('user');
      const parsed = raw ? JSON.parse(raw) : null;
      return genderEmoji(parsed?.gender || null);
    } catch {
      return '';
    }
  });

  return (
    <>
      {/* --- Fondo propio de Room: interfaz moderna estilo Home --- */}
      <style>{`
        /* Neutraliza cualquier edge-blur heredado bajo este root */
        #roomRoot.edge-blur-bg, #roomRoot .edge-blur-bg { position:static !important; }
        #roomRoot.edge-blur-bg::before, #roomRoot.edge-blur-bg::after,
        #roomRoot .edge-blur-bg::before, #roomRoot .edge-blur-bg::after {
          content:none !important; display:none !important;
        }

        #roomRoot {
          position:relative;
          min-height:100dvh;
          isolation:isolate;
          overflow:hidden;
          color:#fff;
          background:
            radial-gradient(circle at 50% -8%, rgba(218,165,32,.16), transparent 22%),
            radial-gradient(circle at 12% 18%, rgba(183,44,255,.25), transparent 34%),
            radial-gradient(circle at 84% 64%, rgba(138,43,226,.20), transparent 34%),
            linear-gradient(180deg, #03000b 0%, #100020 48%, #05000d 100%);
        }

        #roomRoot::before{
          content:"";
          position:absolute;
          inset:0;
          z-index:0;
          pointer-events:none;
          background-image:
            radial-gradient(circle at 10% 8%, rgba(255,255,255,.85) 0 1px, transparent 1.8px),
            radial-gradient(circle at 78% 7%, rgba(255,215,0,.75) 0 1px, transparent 1.8px),
            radial-gradient(circle at 94% 20%, rgba(183,44,255,.76) 0 1px, transparent 1.8px),
            radial-gradient(circle at 24% 48%, rgba(255,255,255,.45) 0 1px, transparent 1.8px),
            radial-gradient(circle at 68% 56%, rgba(255,215,0,.50) 0 1px, transparent 1.8px),
            radial-gradient(circle at 44% 84%, rgba(255,255,255,.42) 0 1px, transparent 1.8px);
          opacity:.58;
        }

        #roomRoot::after{
          content:"";
          position:absolute;
          inset:0;
          z-index:1;
          pointer-events:none;
          background:
            radial-gradient(circle at 50% 12%, rgba(218,165,32,.12), transparent 30%),
            radial-gradient(circle at 50% 78%, rgba(75,0,130,.28), transparent 48%);
          filter: blur(12px) saturate(1.08);
          opacity:1;
        }

        #roomRoot::marker { content: none; }
        #roomRoot .violet-haze{ display:none; }

        @media (prefers-reduced-motion: reduce){
          #roomRoot *{ scroll-behavior:auto; }
        }
      `}</style>

      <div id="roomRoot">

        {/* Contenido */}
        <div style={styles.container}>
          <div style={styles.topBar}>
            <button type="button" onClick={goBackHome} style={styles.topCircleButton} aria-label={t('common:actions.back')}>
              ←
            </button>
            <div style={styles.topTitleWrap}>
              <div style={styles.kicker}>{t('room.kicker')}</div>
              <div style={styles.titleRow}>
                <h1 style={styles.topTitle}>{displaySigno} <span style={styles.topEmoji}>{emoji}</span></h1>
                <button
                  type="button"
                  onClick={goToInfo}
                  style={styles.infoMiniButton}
                  title={t('room.actions.info')}
                  aria-label={t('room.actions.info')}
                >
                  {t('room.actions.info')}
                </button>
              </div>
              <p style={styles.topSubtitle}><span style={styles.onlineDot}>●</span> {t('room.liveChat')}</p>
            </div>
          </div>

          {/* Chat (sin fondo extra) */}
          <ChatRoom room={roomId} showHeader={false} signEmoji={emoji} genderEmoji={gEmoji} />
        </div>
      </div>
    </>
  );
};

const styles = {
  container: {
    paddingLeft: 12,
    paddingRight: 12,
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 14px)',
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
    minHeight: '100dvh',
    position: 'relative',
    overflowX: 'hidden',
    backgroundColor: 'transparent',
    zIndex: 3,
    boxSizing: 'border-box',
  },
  topBar: {
    display: 'grid',
    gridTemplateColumns: '40px minmax(0, 1fr)',
    alignItems: 'center',
    gap: 9,
    marginBottom: 12,
    padding: '10px 10px',
    borderRadius: 24,
    border: '1px solid rgba(218,165,32,.32)',
    background: 'linear-gradient(145deg, rgba(7,0,19,.72), rgba(31,5,68,.50))',
    boxShadow: '0 18px 42px rgba(0,0,0,.34), inset 0 1px 0 rgba(255,255,255,.08)',
    backdropFilter: 'blur(16px) saturate(1.12)',
    WebkitBackdropFilter: 'blur(16px) saturate(1.12)',
  },
  topCircleButton: {
    width: 38,
    height: 38,
    minWidth: 38,
    borderRadius: '50%',
    border: '1px solid rgba(218,165,32,.44)',
    background: 'radial-gradient(circle at 34% 22%, rgba(255,255,255,.14), transparent 34%), rgba(10,0,26,.66)',
    color: '#FFE8A3',
    fontSize: 21,
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
    lineHeight: 1,
    padding: 0,
    boxShadow: '0 12px 28px rgba(0,0,0,.32)',
    boxSizing: 'border-box',
  },
  topTitleWrap: {
    minWidth: 0,
  },
  kicker: {
    color: '#F3C96B',
    fontSize: '.58rem',
    fontWeight: 950,
    letterSpacing: '.18em',
    textTransform: 'uppercase',
    marginBottom: 3,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 7,
    minWidth: 0,
  },
  topTitle: {
    margin: 0,
    color: '#F3C96B',
    fontFamily: 'Georgia, serif',
    fontSize: 'clamp(1.45rem, 6.9vw, 2.25rem)',
    lineHeight: .96,
    letterSpacing: '.01em',
    textShadow: '0 0 24px rgba(218,165,32,.34)',
    whiteSpace: 'nowrap',
    minWidth: 0,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  topEmoji: {
    color: '#B72CFF',
    fontFamily: 'system-ui, sans-serif',
  },
  topSubtitle: {
    margin: '8px 0 0',
    color: 'rgba(255,255,255,.78)',
    fontSize: 13,
    fontWeight: 850,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  onlineDot: {
    color: '#27e38a',
    textShadow: '0 0 12px rgba(39,227,138,.85)',
    margin: '0 4px',
  },
  infoMiniButton: {
    position: 'relative',
    zIndex: 1,
    borderRadius: 999,
    padding: '6px 9px',
    border: '1px solid rgba(255,215,0,.40)',
    background: 'linear-gradient(135deg,#8A2BE2,#6F1CCB,#DAA520)',
    color: '#fff',
    fontSize: '.68rem',
    fontWeight: 950,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    boxShadow: '0 8px 18px rgba(0,0,0,.24), 0 0 14px rgba(218,165,32,.16)',
  },
};

export default RoomScreen;
